import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { Alert, AlertSize, AlertType } from '../../components/Alert/Alert';
import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { ModalDecorator } from '../../components/ModalDecorator/ModalDecorator';
import { Shape2, Shape2Icon, Shape2Tone } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { ValidationErrorData } from '../../typings/common';
import { GALLERY_MODAL_PREVIEW_Z_INDEX_LEVEL } from '../common/typings';

import './GalleryPreviewModalPopup.css';
import * as keyset from './GalleryPreviewModalPopup.i18n';

const BLOCK = 'gallery-preview-modal-popup';
const i18n = i18nFactory(keyset);

interface GalleryPreviewModalPopupProps {
    hasPrevious?: boolean;
    hasNext?: boolean;
    header: string;
    itemCollectionIndex: number;
    error?: ValidationErrorData;

    previewItem: (collectionIndex?: number) => void;
    applyItem: (collectionIndex?: number) => void;
}

export class GalleryPreviewModalPopup extends React.PureComponent<GalleryPreviewModalPopupProps> {
    render() {
        const { hasNext, hasPrevious, header } = this.props;

        return (
            <ModalDecorator
                cls={BLOCK}
                theme={'dark-paranja'}
                visible={true}
                hasClose={true}
                onClose={this.onModalPopupClose}
                header={header}
                footer={this.getModalPopupFooter()}
                zIndexGroupLevel={GALLERY_MODAL_PREVIEW_Z_INDEX_LEVEL}
            >
                <div className={Bem.cls(BLOCK, 'content')}>
                    {this.props.children}
                    {hasPrevious && (
                        <ButtonDecorator
                            cls={Bem.cls(BLOCK, 'arrow-left')}
                            theme={'clear'}
                            onClick={this.onPreviousPreviewArrowClicked}
                        >
                            <Shape2
                                width={24}
                                height={43}
                                tone={Shape2Tone.white}
                                cls={Bem.cls(BLOCK, 'arrow')}
                                icon={Shape2Icon.arrowLeftBig}
                            />
                        </ButtonDecorator>
                    )}
                    {hasNext && (
                        <ButtonDecorator
                            cls={Bem.cls(BLOCK, 'arrow-right')}
                            theme={'clear'}
                            onClick={this.onNextPreviewArrowClicked}
                        >
                            <Shape2
                                width={24}
                                height={43}
                                tone={Shape2Tone.white}
                                cls={Bem.cls(BLOCK, 'arrow')}
                                icon={Shape2Icon.arrowRightBig}
                            />
                        </ButtonDecorator>
                    )}
                </div>
            </ModalDecorator>
        );
    }

    private getModalPopupFooter = () => {
        return (
            <>
                {this.renderError()}
                <div className={Bem.cls(BLOCK, 'footer')}>
                    <ButtonDecorator
                        theme={'action'}
                        cls={Bem.cls(BLOCK, 'control-button')}
                        text={i18n('preview-select')}
                        size={'s'}
                        onClick={this.onApplyItemFromModalPopup}
                    />
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'control-button')}
                        theme={'normal'}
                        text={i18n('preview-cancel')}
                        size={'s'}
                        onClick={this.onModalPopupClose}
                    />
                </div>
            </>
        );
    };

    private onModalPopupClose = () => {
        this.props.previewItem();
    };

    private onApplyItemFromModalPopup = () => {
        const { itemCollectionIndex, applyItem } = this.props;

        applyItem(itemCollectionIndex);
    };

    private onPreviousPreviewArrowClicked = () => {
        const { itemCollectionIndex } = this.props;

        this.props.previewItem(itemCollectionIndex - 1);
    };

    private onNextPreviewArrowClicked = () => {
        const { itemCollectionIndex } = this.props;

        this.props.previewItem(itemCollectionIndex + 1);
    };

    private renderError = (): React.ReactNode => {
        const { error } = this.props;

        if (!error) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'error')}>
                <Alert type={AlertType.error} size={AlertSize.s}>
                    {error.text}
                </Alert>
            </div>
        );
    };
}
